import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #pendingFocus = false;
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('cart:rendered', this.#handleCartRendered);
    this.addEventListener('close', this.#handleClose, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('cart:rendered', this.#handleCartRendered);
    this.removeEventListener('close', this.#handleClose, true);
  }

  #handleCartAdd = (event) => {
    if (!(event instanceof CartAddEvent) || event.detail.data.didError) return;
    if (this.hasAttribute('auto-open')) {
      this.#pendingFocus = true;
      this.dataset.added = '';
      this.showDialog(this.#resolveTrigger(event));
    }
  };

  /**
   * Resolve the control that should receive focus when the drawer closes.
   * @param {CartAddEvent} event
   * @returns {Element | null}
   */
  #resolveTrigger(event) {
    const source = event.target instanceof Element ? event.target : null;
    const active = document.activeElement;
    const candidate = source?.contains(active)
      ? active
      : source?.closest('button') ?? source?.querySelector('button[type="submit"]') ?? active;

    let owner = candidate?.closest('dialog')?.parentElement;
    while (owner && !(owner instanceof DialogComponent)) owner = owner.parentElement;

    return owner && owner !== this ? owner.trigger ?? candidate : candidate;
  }

  #handleClose = () => {
    this.#pendingFocus = false;
    delete this.dataset.added;
  };

  #handleCartRendered = () => {
    if (!this.#pendingFocus) return;
    this.#pendingFocus = false;
    this.dataset.added = '';
    const dialog = this.querySelector('dialog[open]');
    if (dialog && !document.activeElement?.closest('dialog:not(.cart-drawer__dialog)')) {
      dialog.querySelector('[ref="closeButton"]')?.focus({ preventScroll: true });
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
